import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchNoticesPage, fetchNoticeAudienceUsers } from '@services/registry';

export function useNotices({ tenantId, page = 1, pageSize = 10 }) {
    const cursorByPageRef = useRef(new Map([[1, null]]));

    useEffect(() => {
        cursorByPageRef.current = new Map([[1, null]]);
    }, [tenantId, pageSize]);

    const noticesQuery = useQuery({
        queryKey: ['notices', tenantId, page, pageSize],
        queryFn: async () => {
            const cursor = cursorByPageRef.current.get(page);
            if (page > 1 && typeof cursor === 'undefined') {
                throw new Error('Pagination cursor missing. Please go page-by-page.');
            }
            const res = await fetchNoticesPage({
                tenantId,
                pageSize,
                cursor: cursor ?? null,
            });
            cursorByPageRef.current.set(page + 1, res.nextCursor || null);
            return res;
        },
        enabled: !!tenantId,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const audienceQuery = useQuery({
        queryKey: ['noticeAudienceUsers', tenantId],
        queryFn: () => fetchNoticeAudienceUsers({ tenantId }),
        enabled: !!tenantId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    return {
        rows: noticesQuery.data?.rows || [],
        total: noticesQuery.data?.total || 0,
        analytics: noticesQuery.data?.analytics || {
            total: 0,
            byType: { notice: 0, announcement: 0 },
            byStatus: { pending: 0, scheduled: 0, sent: 0 },
        },
        audienceUsers: audienceQuery.data || [],
        loading: noticesQuery.isLoading || audienceQuery.isLoading,
        error: noticesQuery.error || audienceQuery.error || null,
    };
}
